// src/contexts/pedidos/PedidoContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';
import { useToken } from '../geral/TokenContext';
import { useSSE } from '../geral/SSEContext';
import { useConfigPedido } from './ConfigPedidoContext.js';
import { formatDateInput } from '../../utils/dateTimeUtils';
import {
    createPedido,
    deletePedidoService,
    existeNotaPorPedidoService,
    getDashPedido,
    getPedidoByIdService,
    updatePedidoService,
} from '../../services/pedidos/PedidoService.js';
import { StatusPedido } from '../../models/pedidos/StatusPedido';

const PedidoContext = createContext();

export const usePedido = () => useContext(PedidoContext);

const subtrairHoras = (data, horas) => {
    const novaData = new Date(data);
    novaData.setHours(novaData.getHours() - horas);
    return novaData;
};

export const PedidoProvider = ({ children }) => {
    const [pedidos, setPedidos] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();
    const { token } = useToken();
    const { subscribeToEvent } = useSSE();
    const { configPedido } = useConfigPedido();
    const [temPedidoNovo, setTemPedidoNovo] = useState(false);
    const audioRef = useRef(null);
    const alertaIntervalRef = useRef(null);
    const [pedidoSel, setPedidoSel] = useState({
        id: 0,
        nomeCliente: "",
        statusPedido: StatusPedido.EmProducao,
        tipoEntrega: { id: 1, descricao: "", taxaEntrega: 0 },
        tipoPagamento: { id: 1, descricao: "" },
        enderecoCliente: "",
        pedidoItens: [],
    });

    const iniciarAlertaSonoro = useCallback(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio('/sounds/novo-pedido.mp3');
            audioRef.current.volume = 0.7;
        }

        if (alertaIntervalRef.current) {
            return;
        }

        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});

        alertaIntervalRef.current = setInterval(() => {
            if (!audioRef.current) return;
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
        }, 10000);
    }, []);

    const pararAlertaSonoro = useCallback(() => {
        if (alertaIntervalRef.current) {
            clearInterval(alertaIntervalRef.current);
            alertaIntervalRef.current = null;
        }
    }, []);

    const isStatusEmAnalise = (statusPedido) => {
        const statusDescricao = String(statusPedido || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();

        return statusDescricao === 'em analise';
    };

    const refreshPedidoAlerta = useCallback(async (pedidosData = null) => {
        try {
            let pedidosParaAlerta = pedidosData;

            // Reaproveita os pedidos já consultados pelo painel para evitar uma segunda chamada à API.
            if (!pedidosParaAlerta) {
                const tempoPedidoHoras = configPedido?.tempoPedido !== null && configPedido?.tempoPedido !== undefined && configPedido?.tempoPedido !== ''
                    ? Number(configPedido.tempoPedido)
                    : 12;
                const hoje = formatDateInput(new Date());
                const inicio = subtrairHoras(new Date(), tempoPedidoHoras);
                const fim = new Date(hoje + "T23:59:59");

                pedidosParaAlerta = await getDashPedido({
                    dataHoraI: inicio,
                    dataHoraF: fim,
                });
            }

            const grupoEmAnalise = pedidosParaAlerta?.find((g) => isStatusEmAnalise(g?.statusPedido));
            const possuiPedidoEmAnalise = (grupoEmAnalise?.pedidos?.length || 0) > 0;

            setTemPedidoNovo(possuiPedidoEmAnalise);

            if (possuiPedidoEmAnalise) {
                iniciarAlertaSonoro();
            } else {
                pararAlertaSonoro();
            }
        } catch (error) {
            console.error("Erro ao atualizar alerta global de pedidos:", error);
        }
    }, [configPedido, iniciarAlertaSonoro, pararAlertaSonoro]);

    const findPedido = useCallback(async (pedidoId) => {
        try {
            const result = await getPedidoByIdService(pedidoId);
            setPedidoSel(result);
            return result;
        } catch (error) {
            console.error("Erro ao buscar pedidos:", error);
            showMessage(`Erro ao buscar pedidos! ${error?.message || error}`, 'danger');
        }
    }, [showMessage]);

    const addPedido = async (pedido) => {
        try {
            const result = await createPedido(pedido);
            setPedidos([...pedidos, result]);
            showAlert('Pedido adicionado com sucesso!');
        } catch (error) {
            console.error("Erro ao adicionar pedido:", error);
            showMessage('Erro ao adicionar pedido!\n' + error, 'danger');
        }
    };

    const updatePedido = async (pedido) => {
        try {
            const result = await updatePedidoService(pedido);
            setPedidos((pedidosAtuais) =>
                pedidosAtuais.map((p) => (p.id === result.id ? result : p))
            );
            showAlert('Pedido alterado com sucesso!');
        } catch (error) {
            console.error("Erro ao atualizar pedido:", error);
            showAlert('Erro ao alterar pedido!', 'danger');
        }
    };

    const deletePedido = async (id) => {
        try {
            await deletePedidoService(id);
            setPedidos(pedidos.filter((p) => p.id !== id));
            showAlert('Pedido excluido com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir pedido:", error);
            showAlert('Erro ao excluir pedido!', 'danger');
        }
    };

    const getPedidoById = (pedidoId) => {
        return getPedidoByIdService(pedidoId);
    };

    const existeNotaPorPedido = async (pedidoId) => {
        try {
            const result = await existeNotaPorPedidoService(pedidoId);

            if (typeof result === 'boolean') {
                return result;
            }

            if (typeof result === 'string') {
                return result.trim().toLowerCase() === 'true';
            }

            return Boolean(result);
        } catch (error) {
            console.error("Erro ao verificar nota por pedido:", error);
            showMessage(`Erro ao verificar nota do pedido! ${error?.message || error}`, 'danger');
            throw error;
        }
    };

    useEffect(() => {
        if (!token || token === 'null') {
            setTemPedidoNovo(false);
            pararAlertaSonoro();
            return;
        }

        refreshPedidoAlerta();
    }, [token, refreshPedidoAlerta, pararAlertaSonoro]);

    useEffect(() => {
        if (!token || token === 'null') {
            return undefined;
        }

        return subscribeToEvent('pedido', () => {
            refreshPedidoAlerta();
        });
    }, [token, refreshPedidoAlerta, subscribeToEvent]);

    useEffect(() => {
        return () => {
            pararAlertaSonoro();
        };
    }, [pararAlertaSonoro]);

    return (
        <PedidoContext.Provider
            value={{
                findPedido,
                pedidos,
                setPedidos,
                pedidoSel,
                setPedidoSel,
                addPedido,
                updatePedido,
                deletePedido,
                getPedidoById,
                existeNotaPorPedido,
                temPedidoNovo,
                refreshPedidoAlerta,
            }}
        >
            {children}
        </PedidoContext.Provider>
    );
};
